import axios from "axios";
import i18n from "../i18n";
import { API_URL } from "./config";

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// ───────────────────────────────────────
// Request interceptor — attach language
// ───────────────────────────────────────
api.interceptors.request.use((config) => {
  config.headers["Accept-Language"] = i18n.language || "ca";
  return config;
});

// ───────────────────────────────────────
// Response interceptor — auto-refresh on 401
// ───────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || "";

    // Handle demo mode readonly
    if (error.response?.status === 403 && error.response?.data?.detail === "demo_mode_readonly") {
      return Promise.reject(error);
    }

    // Skip refresh for: login, refresh, public, voter, scrutiny endpoints
    const skipRefresh =
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retry ||
      url.includes("/auth/refresh") ||
      url.includes("/entities/login") ||
      url.includes("/scrutiny/") ||
      url.includes("/public/") ||
      url.includes("/voter/");

    if (skipRefresh) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => {
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      processQueue(null);
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      localStorage.removeItem("entity_id");
      localStorage.removeItem("entity_name");
      localStorage.removeItem("entity_logo");
      localStorage.removeItem("entity_language");
      window.dispatchEvent(new Event("auth:expired"));
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ───────────────────────────────────────
// Auth
// ───────────────────────────────────────
export const login = (email, password) =>
  api.post("/entities/login", { email, password });

export const register = (name, email, password, language = "ca", notification_email = null) =>
  api.post("/entities/register", { name, email, password, language, ...(notification_email ? { notification_email } : {}) });

export const getCurrentEntity = () => api.get("/entities/me");
export const updateEntity = (data) => api.put("/entities/me", data);

// DPA
export const getDpaStatus = () => api.get("/entity/dpa-status");
export const getDpaDocument = () => api.get("/entity/dpa-document");
export const acceptDpa = () => api.post("/entity/dpa-accept");

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats");

// Password
export const changePassword = (data) => api.put("/entities/change-password", data);

// Consultes
export const getConsultes = () => api.get("/consultes");
export const getConsulta = (id) => api.get(`/consultes/${id}`);
export const createConsulta = (data) => api.post("/consultes", data);
export const updateConsulta = (id, data) => api.put(`/consultes/${id}`, data);
export const activateConsulta = (id) => api.put(`/consultes/${id}/activate`);
export const closeConsulta = (id) => api.put(`/consultes/${id}/close`);

// Census
export const uploadCensus = (consultaId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/census/upload/${consultaId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const getCensusUploadStatus = (consultaId) =>
  api.get(`/census/upload-status/${consultaId}`);
export const clearCensusUploadStatus = (consultaId) =>
  api.delete(`/census/upload-status/${consultaId}`);
export const getCensusStats = (consultaId) =>
  api.get(`/census/${consultaId}/stats`);
export const deleteCensus = (consultaId) =>
  api.delete(`/census/${consultaId}`);
export const downloadCensusTemplate = (consultaId) =>
  api.get(`/census/${consultaId}/template`, { responseType: 'blob' });
export const downloadPdfGpt = (consultaId) =>
  api.get(`/results/${consultaId}/pdf-gpt`, { responseType: 'blob' });

// Key Regeneration
export const requestKeyRegeneration = () => api.post("/entities/request-key-regeneration");
export const cancelKeyRegeneration = () => api.post("/entities/cancel-key-regeneration");
export const getNewKey = () => api.get("/entities/new-key");
export const confirmKeyViewed = () => api.post("/entities/confirm-key-viewed");
export const confirmKeys = () => api.post("/entities/confirm-keys");

// Image upload
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/upload/image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Voting (public)
export const getPublicConsulta = (consultaId) =>
  api.get(`/public/consultes/${consultaId}`);
export const verifyVoter = (dni, consultaId, birthDate) =>
  api.post("/public/verify-voter", { dni, consulta_id: consultaId, birth_date: birthDate });
export const submitVote = (data) => api.post("/public/vote", data);

// Results
export const getResults = (consultaId) => api.get(`/results/${consultaId}`);
export const getBlockchainTab = (consultaId) =>
  api.get(`/results/${consultaId}/blockchain`);
export const exportResults = (consultaId) =>
  api.get(`/results/${consultaId}/export`);
export const sendResultsPdf = (consultaId, pdfBlob) => {
  const formData = new FormData();
  formData.append("pdf_file", pdfBlob, "resultats.pdf");
  return api.post(`/consultes/${consultaId}/send-results-pdf`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Voter Registration
export const registerVoter = (dni, birthDate, email) =>
  api.post("/entities/register-voter", { dni, birth_date: birthDate, email });
export const registerVoterInperson = (dni, birthDate) =>
  api.post("/entities/register-voter-inperson", { dni, birth_date: birthDate });
export const resetVoterPassword = (dni, birthDate, email) =>
  api.post("/entities/reset-voter-password", { dni, birth_date: birthDate, email });
export const resetVoterPasswordInperson = (dni, birthDate) =>
  api.post("/entities/reset-voter-password-inperson", { dni, birth_date: birthDate });
export const getRegisteredVotersStats = () =>
  api.get("/entities/registered-voters/stats");
export const toggleOnlineRegistration = () =>
  api.put("/entities/toggle-online-registration");
export const getVoterRegistrationInfo = (token) =>
  api.get(`/voter/registration-info/${token}`);
export const voterSetPassword = (token, password, email) =>
  api.post("/voter/set-password", { token, password, email });
export const verifyVoterPassword = (consultaId, dni, birthDate, password) =>
  api.post("/public/verify-password", { consulta_id: consultaId, dni, birth_date: birthDate, password });

// Integrity
export const checkIntegrity = (consultaId) =>
  api.get(`/integrity/${consultaId}`);

// Audit
export const getPublicAudit = (consultaId) =>
  api.get(`/audit/public/${consultaId}`);
export const downloadAuditPdf = (consultaId) =>
  api.get(`/audit/public/${consultaId}/pdf`, { responseType: 'blob' });

// Voter Tracking
export const downloadVoterTracking = (consultaId) =>
  api.get(`/results/${consultaId}/tracking-download`, { responseType: 'blob' });

// Verify Vote Settings
export const updateVerifyVoteSettings = (consultaId, data) =>
  api.put(`/audit/verify-vote-settings/${consultaId}`, data);

// Private Audit
export const downloadPrivateAudit = (consultaId) =>
  api.get(`/results/${consultaId}/private-audit-download`, { responseType: 'blob' });

// Audit Verification
export const requestAuditVerification = (consultaId, data) =>
  api.post(`/audit/request-verification/${consultaId}`, data);
export const getAuditVerificationHistory = (consultaId) =>
  api.get(`/audit/verification-history/${consultaId}`);
export const getAuditVerificationInfo = (token) =>
  api.get(`/audit/verify-token/${token}`);
export const verifyAuditVote = (token, data) =>
  api.post(`/audit/verify-vote/${token}`, data);

// Hand Vote
export const downloadHandVote = (consultaId) =>
  api.get(`/results/${consultaId}/hand-vote-download`, { responseType: 'blob' });

// Blockchain
export const retryScrutinyBlockchain = (consultaId) =>
  api.post(`/scrutiny/retry-blockchain/${consultaId}`);

// Re-scrutiny
export const resetScrutiny = (consultaId) =>
  api.post(`/scrutiny/reset/${consultaId}`);

export default api;
